import { FloatButton, message } from 'antd';
import appLogoDark from 'assets/logos/logo.png';
import axios from 'axios';
import clsx from 'clsx';
import InfoCircleOutlineIcon from 'components/AppIcon/InfoCircleOutlineIcon';
import AppMainNavigationMenu from 'components/AppMainNavigationMenu';
import AppMainNavigationTabBar from 'components/AppMainNavigationTabBar';
import ScrollRestoration from 'components/ScrollRestoration';
import SplashScreen from 'components/SplashScreen';
import { TOKEN } from 'features/auth/constants';
import { getAuthToken } from 'features/auth/helpers';
import { setUserInfo } from 'features/auth/slice';
import { TUserInfo } from 'features/auth/types';
import { KeycloakRequestBaseURL } from 'lib/keycloak/keycloak';
import { useAppDispatch } from 'lib/stores/hook';
import { memo, Suspense, useCallback, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getPath } from 'routes/router-paths';

const MainLayout = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [messageApi, contextHolder] = message.useMessage();

  const refreshToken = getAuthToken(TOKEN.REFRESH_TOKEN);
  const accessToken = getAuthToken(TOKEN.ACCESS_TOKEN);

  useEffect(() => {
    if (refreshToken) return;
    navigate(getPath('loginPage', location.pathname));
  }, [refreshToken, location.pathname, messageApi, navigate]);

  const fetchUserInfo = useCallback(async () => {
    try {
      const userData = await axios.request<unknown, TUserInfo>({
        url: `${KeycloakRequestBaseURL}userinfo`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      dispatch(setUserInfo(userData));
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  }, [accessToken, dispatch]);

  useEffect(() => {
    void fetchUserInfo();
  }, [fetchUserInfo]);

  return (
    <div className="flex h-screen bg-[#043871]">
      <aside className="w-45 shrink-0 overflow-y-auto overflow-x-hidden bg-[#043871] pb-4 pt-5.5 dark:bg-[--bg-dark]">
        <figure
          className="mb-5 block w-full cursor-pointer text-center"
          onClick={() => navigate(getPath('portalPage'))}
        >
          <img src={appLogoDark} alt="FAFU" width={66} />
        </figure>

        <nav role="navigation">
          <AppMainNavigationMenu />
        </nav>
      </aside>

      <div className="w-full flex-1 overflow-x-hidden rounded-ss-2xl bg-white">
        <header className="sticky top-0 z-10 bg-white px-12">
          <AppMainNavigationTabBar />
        </header>
        <div className="app-container min-h-[calc(100dvh-76px-48px)] py-6">
          <Suspense
            fallback={
              <ScrollRestoration>
                <SplashScreen fullScreen />
              </ScrollRestoration>
            }
          >
            <Outlet />
          </Suspense>
        </div>
        <div className="app-container flex items-center justify-between gap-4 py-4 text-xs font-normal leading-[16px] text-[#858585]">
          <span>Copyright © 2025</span>
          <div className="flex items-center justify-end gap-2">
            <span>Hướng dẫn</span>
            <div className="h-1 w-1 rounded-full bg-[#858585]" />
            <span>Hỗ trợ</span>
          </div>
          <FloatButton
            icon={<InfoCircleOutlineIcon className="h-5 w-5" />}
            type="primary"
            style={{ insetInlineEnd: 48, bottom: 44 }}
            className={clsx(
              'h-8 w-8 !bg-[#00B2FF]',
              '[&_.ant-float-btn-body]:!border-none [&_.ant-float-btn-body]:!bg-[#00B2FF] [&_.ant-float-btn-body]:hover:opacity-90',
              '[&_.ant-float-btn-icon]:!m-0 [&_.ant-float-btn-icon]:flex [&_.ant-float-btn-icon]:items-center [&_.ant-float-btn-icon]:justify-center'
            )}
          />
        </div>
      </div>
      {contextHolder}
    </div>
  );
};

export default memo(MainLayout);
